import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateChild } from '@angular/router';
import { Injectable, Inject } from '@angular/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { McbRouteData } from '../../routing/types';
import { UserIdentity } from '../types';
import { NavigationService } from '../../ui/services/navigation.service';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AuthorizationGuard implements CanActivate, CanActivateChild {
    constructor(
        public navService: NavigationService,
        private authService: AuthenticationService,
    ) {
    }

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivateRoute(route, state);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivateRoute(childRoute, state);
    }

    private async canActivateRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const authenticated = this.authService.isUserAuthenticated();

        if (authenticated) {
            let user = this.authService.getUser();
            if (user instanceof Observable) {
                user = await user.toPromise();
            }

            if (this.shouldRedirectToChangePasswordPage(user, route)) {
                this.navService.goToSetPasswordPage();
                return false;
            } else if (this.shouldRedirectToChooseOrganizationPage(user, route)) {
                this.navService.goToChooseOrganizationPage();
                return false;
            } else if (!this.hasUserPermissionToViewPage(user, route)) {
                this.navService.goToDashboardPage();
                return false;
            }
        } else {
            const goingToSigninPage = this.navService.isLoginPageRoute(route);
            if (!goingToSigninPage) {
                this.navService.goToSigninPage();
                return false;
            }
        }

        return true;
    }

    private shouldRedirectToDashboardPage(user: UserIdentity, route: ActivatedRouteSnapshot) {
        return (user.activeOrganizationId && this.navService.isChooseOrganizationRoute(route)) ||
            (!user.forcePasswordChange && this.navService.isChangePasswordRoute(route));
    }

    private shouldRedirectToChooseOrganizationPage(user: UserIdentity, route: ActivatedRouteSnapshot) {
        return !user.activeOrganizationId &&
            !user.forcePasswordChange &&
            !this.navService.isChooseOrganizationRoute(route);
    }

    private shouldRedirectToChangePasswordPage(user: UserIdentity, route: ActivatedRouteSnapshot) {
        return user.forcePasswordChange &&
            !this.navService.isChangePasswordRoute(route);
    }

    private hasUserPermissionToViewPage(user: UserIdentity, route: ActivatedRouteSnapshot) {
        let currentState = route;
        do {
            const data: McbRouteData = currentState.data;
            const perm = data.permission;
            if (perm) {
                if (!user.permissions.some(x => x === perm)) {
                    this.navService.goToDashboardPage();
                    // Inform developer to debuge the app
                    console.log('you have no permission to access to this route:');
                    console.log(route);
                    return false;
                }
            }
            currentState = currentState.children[0];
        } while (currentState);

        return true;
    }
}
